import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Merge, RefreshCw } from "lucide-react";

const STATUT = { active: "actif", actif: "actif", essai: "essai", suspendu: "suspendu", annule: "annulé", a_reviser: "à réviser" };

// Fusionne des fiches de la MÊME personne (verrou : taper le nom). Garde la fiche la plus récente signée/payée.
export async function mergeContracts(rows, { allowDifferentNames = false } = {}) {
  const names = [...new Set(rows.map((r) => (r.full_name || "").trim()).filter(Boolean))];
  const detail = rows.map((r) => `• ${r.full_name} — ${r.discipline || "?"} — ${STATUT[r.statut] || r.statut || "?"}${r.stripe_subscription_id ? " — abonnement Stripe" : ""}`).join("\n");
  const typed = window.prompt(`Fusionner ${rows.length} fiches en UNE seule ?\n\n${detail}\n\nLa fiche principale (la plus récente signée / payée) garde l'historique ; les doublons passent au statut « fusionné » et leurs abonnements Stripe en double sont annulés (jamais celui de la principale).\n\n🔒 Pour confirmer, tape exactement le nom : ${names.join(" / ")}`);
  if (typed === null) return null;
  const { data } = await api.post("/student-contracts/merge", {
    contract_ids: rows.map((r) => r.id), confirm_name: typed.trim(), allow_different_names: allowDifferentNames || names.length > 1,
  }, { timeout: 60000 });
  return data;
}

export default function MergeDuplicatesPanel({ onMerged }) {
  const [groups, setGroups] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/student-contracts/merge-candidates"); setGroups(data.groups || []); }
    catch { setGroups([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const merge = async (g, idx) => {
    setBusy(idx);
    try {
      const res = await mergeContracts(g.contracts, { allowDifferentNames: !g.same_name });
      if (res) { toast.success(`Fiches fusionnées — principale : ${res.kept?.full_name || res.keep_id || "ok"}`); load(); onMerged?.(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Fusion refusée"); }
    finally { setBusy(null); }
  };

  if (!groups || groups.length === 0) return null;
  return (
    <div className="mt-6 border border-violet-200 bg-violet-50 rounded-xl p-4" data-testid="merge-duplicates-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold text-violet-900 flex items-center gap-2"><Merge size={16} /> Clients en double détectés ({groups.length})</h2>
        <button onClick={load} data-testid="merge-reload-btn" className="text-xs text-violet-700 inline-flex items-center gap-1 hover:underline"><RefreshCw size={12} /> Actualiser</button>
      </div>
      <p className="text-xs text-violet-800/80 mt-1">Même nom (ou même courriel avec prénoms semblables). Vérifie chaque cas, puis fusionne : une seule fiche à activer, aucun double prélèvement.</p>
      <div className="mt-3 space-y-2">
        {groups.map((g, idx) => (
          <div key={idx} className="bg-white border border-violet-100 rounded-lg p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`merge-group-${idx}`}>
            <div className="min-w-0">
              <div className="font-semibold text-zinc-900 text-sm">{g.full_name}{!g.same_name && <span className="ml-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">orthographes différentes</span>}</div>
              <ul className="mt-1 text-xs text-zinc-600 space-y-0.5">
                {g.contracts.map((c) => (
                  <li key={c.id}>• {c.full_name} · {c.discipline || "?"} · {STATUT[c.statut] || c.statut} · {c.email || "sans courriel"}{c.stripe_subscription_id ? " · 💳 abonnement" : ""}{c.contract_signed_at ? " · ✍️ signé" : ""}</li>
                ))}
              </ul>
            </div>
            <button onClick={() => merge(g, idx)} disabled={busy === idx} data-testid={`merge-group-btn-${idx}`}
              className="inline-flex items-center gap-1.5 bg-violet-700 hover:bg-violet-800 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
              <Merge size={13} /> {busy === idx ? "Fusion…" : `Fusionner ${g.contracts.length} fiches`}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
